import React, { useEffect, useState } from 'react';
import { Pencil, TriangleAlert } from 'lucide-react';
import { summariseRecruitScores } from '../recruitScore';
import type { RecruitScores } from '../recruitScore';
import type { IntlLike } from '../clientPresentation';
import RecruitRadarChart from './RecruitRadarChart';
import RecruitScoreDialog, { RecruitTotal } from './RecruitScoreDialog';

/**
 * 候選人評估, read in 招募 IDEA's 他做得來嗎 card: the radar (the same chart the
 * drawer draws, labels carrying each score), the 總分 and its band, what to do
 * about it, and the family warning.
 *
 * It lives here and NOT beside the drawer's radar — on a phone that half-width
 * slot cannot hold a total, a band and a warning without the title wrapping one
 * character per line.
 *
 * Scores can be changed from here too: the chart and the 評分 button open the
 * same RecruitScoreDialog the drawer's radar does. Without a client id (nothing
 * to write to) the chart is a plain picture.
 */

export type RecruitScoreSummaryProps = {
  scores: RecruitScores;
  intl: IntlLike;
  clientId?: string | null;
};

const RecruitScoreSummary = (props: RecruitScoreSummaryProps) => {
  const { scores, intl, clientId } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const summary = summariseRecruitScores(scores);
  const [editing, setEditing] = useState(false);

  useEffect(() => setEditing(false), [clientId]);

  const chart = <RecruitRadarChart scores={scores} intl={intl} className="clientv2-recruit-summary-chart" />;

  return (
    <div className="clientv2-recruit-summary" data-testid="recruit-summary">
      {clientId ? (
        <button
          type="button"
          className="clientv2-recruit-radar-chart"
          data-testid="recruit-summary-chart"
          aria-label={t('clientv2-recruit-radar-edit')}
          onClick={() => setEditing(true)}
        >
          {chart}
        </button>
      ) : (
        chart
      )}

      <div className="clientv2-recruit-summary-row">
        <RecruitTotal scores={scores} intl={intl} />
        {clientId && (
          <button
            type="button"
            className="clientv2-recruit-radar-edit"
            data-testid="recruit-summary-edit"
            aria-haspopup="dialog"
            onClick={() => setEditing(true)}
          >
            <Pencil size={12} aria-hidden="true" />
            {t('clientv2-recruit-radar-edit')}
          </button>
        )}
      </div>

      <p className="clientv2-recruit-advice" data-testid="recruit-advice">
        {summary.tier ? t(`clientv2-recruit-tier-${summary.tier}-advice`) : t('clientv2-recruit-total-incomplete')}
      </p>

      {summary.familyConcern && (
        <span className="clientv2-recruit-family-warning" data-testid="recruit-family-warning">
          <TriangleAlert size={12} aria-hidden="true" />
          {t('clientv2-recruit-family-warning')}
        </span>
      )}

      <p className="clientv2-idea-footnote">{t('clientv2-recruit-total-hint')}</p>

      {clientId && (
        <RecruitScoreDialog open={editing} onClose={() => setEditing(false)} clientId={clientId} intl={intl} />
      )}
    </div>
  );
};

export default RecruitScoreSummary;
